import { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import * as api from '../../lib/api/api';
import * as PATH from '../../lib/routes/path';
import PrePurchaseReducer from '../../modules/reducers/PrePurchaseReducer';
import useError from '../useError';
import { ERROR_REFRESH, STATUS_MESSAGE } from '../../lib/Error';
import {
  getPaymentMethod,
  IMP_CODE,
  PRODUCT_NAME,
} from '../../lib/PaymentInfo';
import AffiliateContext from '../../modules/contexts/AffiliateContext';

const IMP = window.IMP;
IMP.init(IMP_CODE);
const PAYMENT_FAIL_MSG = STATUS_MESSAGE.order.requestPay.FAIL;

const usePrePurchase = ({ query }) => {
  const { onAlertStatusError, onHandleInvalidFieldError } = useError();
  const {
    state,
    setPrePurchaseIds,
    setTempOrderInfo,
    setUserInfo,
    onPaymentMethod,
    buyerInfo,
    onChangeValue,
  } = PrePurchaseReducer();
  const { actions: { setIsAffiliateOutlink } } = useContext(AffiliateContext);

  const {
    prePurchaseItemId,
    prePurchaseOptionIds,
    voucherIds,
    defaultPrice,
    finalShippingFee,
    finalPrice,
    email,
    pg,
    pay_method,
  } = state;

  const [merchant_uid, setMerchant_uid] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [toCompletion, setToCompletion] = useState(false);

  const getDataTempOrderInfo = ({
    id,
    optionIds,
    selectedVoucherIds = undefined,
    onSetSelectedVoucherCoupon = undefined,
    setIsLoading = undefined,
  }) => {
    const voucherIdsData = selectedVoucherIds ? selectedVoucherIds : voucherIds;

    if (setIsLoading) setIsLoading(true);
    api
      .tempPrice({
        itemId: id,
        optionIds,
        voucherIds: voucherIdsData,
        includeShippingFee: true,
      })
      .then((response) => {
        setTempOrderInfo({
          data: response.data,
          voucherIds: voucherIdsData,
        });
        onSetSelectedVoucherCoupon && onSetSelectedVoucherCoupon();
      })
      .catch((error) => {
        const {
          status,
          data: { invalidValue },
        } = error.response;
        status === 400
          ? alert(invalidValue)
          : status === 409
          ? alert(STATUS_MESSAGE.order.testPrice[status])
          : alert(ERROR_REFRESH);
      }).finally(() => {
        if (setIsLoading) setIsLoading(false);
      });
  };

  const getDataVoucherIdsInfo = () => {
    const voucherName = '베이비페이스 사전 구매 쿠폰';
    const itemId = sessionStorage.itemId;
    const itemOptionIds = JSON.parse(sessionStorage.optionIds);

    api
      .postVoucherOrderTemplate({ voucherName, itemId, itemOptionIds })
      .then((response) => {
        const { id, optionIds } = response.data.items[0];

        setPrePurchaseIds({ id, optionIds });
        getDataTempOrderInfo({ id, optionIds });
      })
      .catch((error) => {
        const message = STATUS_MESSAGE.order.voucherOrderTemplate;
        onAlertStatusError({ message, error });
      });
  };

  const getDataUserInfo = () => {
    api.getInfo().then((response) => {
      const { data } = response;
      setUserInfo({ data });
    });
  };

  const doGetPaymentInfoInLoop = ({ merchant_uid, handler }) => {
    api.getPaymentInfo(merchant_uid).then((response) => {
      const { status, items } = response.data;
      const { code } = items[0];
      if (status === 'PAID' || status === 'PENDING_DEPOSIT') {
        clearInterval(handler);
        status === 'PAID' && postRegisterPrePurchaseVoucher({ code });
        setIsAffiliateOutlink(false);
        setToCompletion(true);
      }
    });
  };

  const doFailPaymentInLoop = ({ merchant_uid, handler }) => {
    clearInterval(handler);
    api.putForceRefund(merchant_uid);
    setIsLoading(false);
    alert(PAYMENT_FAIL_MSG);
  };

  const doCheckPaymentStatus = () => {
    const { imp_success, error_msg, merchant_uid } = query;

    if (imp_success === 'true') {
      setIsLoading(true);
      setMerchant_uid(merchant_uid);

      let loopCheck = 0;
      const loop = () => {
        loopCheck += 1;
        doGetPaymentInfoInLoop({ merchant_uid, handler });
        if (loopCheck === 3) {
          doFailPaymentInLoop({ merchant_uid, handler });
        }
      };
      const handler = setInterval(loop, 3000);
    } else if (imp_success === 'false') {
      alert(`${PAYMENT_FAIL_MSG} 에러 내용: ${error_msg}`);
    } else {
      return null;
    }
  };

  useEffect(() => {
    getDataVoucherIdsInfo();
    getDataUserInfo();
    doCheckPaymentStatus();
  }, []);

  const doSelectVoucherCoupon = (selectedVoucherIds, onSetSelectedVoucherCoupon, setIsLoading) => {
    getDataTempOrderInfo({
      id: prePurchaseItemId || sessionStorage.itemId,
      optionIds: prePurchaseOptionIds.length > 0 ? prePurchaseOptionIds : JSON.parse(sessionStorage.optionIds),
      selectedVoucherIds,
      onSetSelectedVoucherCoupon: () => onSetSelectedVoucherCoupon(selectedVoucherIds),
      setIsLoading,
    });
  };

  const postRegisterPrePurchaseVoucher = ({ code }) => {
    const { success, error } = STATUS_MESSAGE.voucher.prePurchase;
    api
      .postVoucherCoupon({ code })
      .then(() => alert(success))
      .catch(() => alert(error));
  };

  const doPrePurchaseRequestPay = ({ paymentKey, code }) => {
    if (finalPrice === 0) {
      postRegisterPrePurchaseVoucher({ code });
      setIsAffiliateOutlink(false);
      setToCompletion(true);
    } else {
      // 크롬 팝업 차단 정책 대응: 클릭 이벤트 내에서 즉시 호출
      try {
        IMP.request_pay(
          {
            pg,
            pay_method,
            merchant_uid: paymentKey,
            name: PRODUCT_NAME,
            amount: finalPrice,
            buyer_email: email,
            buyer_name: buyerInfo.name,
            buyer_tel: buyerInfo.mobile,
            notice_url: `${process.env.REACT_APP_BACKEND_URL}/any/payment/iamport`,
            m_redirect_url: `${process.env.REACT_APP_ORIGIN_URL}${PATH.ORDER_PRE_PURCHASE}`,
            vbank_due: moment().clone().add(3, 'day').format('YYYYMMDDhhmm'),
          },
          (rsp) => {
            const { success, merchant_uid, error_msg } = rsp;
            if (success) {
              let loopCheck = 0;
              const loop = () => {
                loopCheck += 1;
                doGetPaymentInfoInLoop({ merchant_uid, handler });
                if (loopCheck === 7) {
                  doFailPaymentInLoop({ merchant_uid, handler });
                }
              };
              const handler = setInterval(loop, 3000);
            } else {
              setIsLoading(false);
              alert(`${PAYMENT_FAIL_MSG} 에러 내용: ${error_msg}`);
            }
          },
        );
      } catch (error) {
        console.error('결제창 호출 오류:', error);
        setIsLoading(false);
        alert(`${PAYMENT_FAIL_MSG} 결제창을 열 수 없습니다. 다시 시도해주세요.`);
      }
    }
  };

  const createPrePurchaseOrder = async () => {
    if (
      buyerInfo.mobile &&
      buyerInfo.mobileError === null &&
      buyerInfo.name &&
      buyerInfo.nameError === null
    ) {
      setIsLoading(true);
      
      api
        .postPrePurchaseOrder({
          prePurchaseItemId,
          prePurchaseOptionIds,
          name: buyerInfo.name,
          email,
          mobile: buyerInfo.mobile,
          paymentMethod: getPaymentMethod({ pay_method, pg }),
          voucherIds,
        })
        .then((response) => {
          const { paymentKey, items } = response.data;
          const { code } = items[0];
          setMerchant_uid(paymentKey);
          // 크롬 팝업 차단 정책 대응: setTimeout을 사용하여 이벤트 루프의 다음 틱에서 실행
          // 사용자 제스처(클릭)를 보존하기 위해 즉시 실행
          setTimeout(() => {
            doPrePurchaseRequestPay({ paymentKey, code });
          }, 0);
        })
        .catch((error) => {
          onHandleInvalidFieldError({ error, errorMsg: STATUS_MESSAGE.order });
          setIsLoading(false);
        });
    } else {
      alert('신청자 정보를 확인해주세요.');
    }
  };

  return {
    defaultPrice,
    finalShippingFee,
    finalPrice,
    buyerInfo,
    pg,
    pay_method,
    merchant_uid,
    isLoading,
    toCompletion,
    onPaymentMethod,
    doSelectVoucherCoupon,
    createPrePurchaseOrder,
    onChangeValue,
  };
};

export default usePrePurchase;
